import React, { useRef } from 'react';
import styled from 'styled-components';
import { X, Download } from 'lucide-react';
import { Apartment } from '../types/Apartment';
import jsPDF from 'jspdf';
import { formatPrice } from '../config/priceConfig';

const TableContainer = styled.div`
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.9));
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 16px;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 1000px;
  width: fit-content;
  min-width: 700px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2), 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  z-index: 100;

  @media (max-width: 850px) {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 12px;
    border-radius: 16px;
    min-width: auto;
    width: 90vw;
    max-width: 90vw;
    max-height: 85vh;
    overflow: hidden;
  }
`;

const ScrollableContent = styled.div`
  max-height: calc(80vh - 120px);
  overflow-y: auto;
  margin: 40px 0;
  padding-right: 8px;
  
  /* Custom scrollbar styling */
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: rgba(59, 130, 246, 0.6) rgba(255, 255, 255, 0.3); /* Firefox */
  
  /* Webkit scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(145deg, rgba(59, 130, 246, 0.8), rgba(59, 130, 246, 0.6));
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.2s ease;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(145deg, rgba(59, 130, 246, 1), rgba(59, 130, 246, 0.8));
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }

  @media (max-width: 850px) {
    max-height: calc(85vh - 100px);
    margin: 30px 0;
    padding-right: 6px;
    
    /* Keep custom scrollbar on mobile */
    scrollbar-width: thin;
    scrollbar-color: rgba(59, 130, 246, 0.6) rgba(255, 255, 255, 0.3);
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.3);
      border-radius: 8px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: linear-gradient(145deg, rgba(59, 130, 246, 0.8), rgba(59, 130, 246, 0.6));
      border-radius: 8px;
      border: 1px solid rgba(255, 255, 255, 0.2);
    }
  }
`;

const ApartmentTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  table-layout: fixed;
`;

const TableHeader = styled.thead`
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.02));
  border-radius: 12px;
`;

const HeaderCell = styled.th`
  padding: 12px 0px;
  text-align: center;
  font-weight: 600;
  font-size: 13px;
  color: #374151;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  
  &:nth-child(1) { width: 100px; } /* NR. (vergrößert für neue Bezeichnungen) */
  &:nth-child(2) { width: 80px; } /* ZIMMER */
  &:nth-child(3) { width: 80px; } /* ETAGE */
  &:nth-child(4) { width: 90px; } /* FLÄCHE */
  &:nth-child(5) { width: 140px; } /* PREIS */
  &:nth-child(6) { width: 110px; } /* STATUS (breiter für Badge) */
  &:nth-child(7) { width: 90px; } /* DETAILS (schmaler) */
  
  &:first-child {
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
  }
  
  &:last-child {
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
  }

  @media (max-width: 850px) {
    padding: 12px 8px;
    font-size: 12px;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ isSelected?: boolean; isAvailable?: boolean }>`
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => 
    props.isSelected 
      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05))' 
      : 'transparent'
  };
  opacity: ${props => props.isAvailable === false ? 0.6 : 1};
  
  &:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(59, 130, 246, 0.02));
    transform: translateY(-1px);
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }
`;

const TableCell = styled.td`
  padding: 12px 0px;
  font-size: 13px;
  color: #374151;
  vertical-align: middle;
  text-align: center;

  @media (max-width: 850px) {
    padding: 10px 3px;
    font-size: 11px;
  }
`;

const ApartmentNumber = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
  text-align: center;
  white-space: nowrap;
`;

const ApartmentDescription = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
  text-align: center;

  @media (max-width: 850px) {
    font-size: 11px;
  }
`;

const PriceDisplay = styled.div`
  font-weight: 600;
  color: #059669;
  font-size: 14px;
  text-align: center;

  @media (max-width: 850px) {
    font-size: 12px;
  }
`;

const StatusPriceDisplay = styled.div<{ status: string }>`
  font-weight: 600;
  font-size: 14px;
  text-align: center;
  
  color: ${props => {
    switch (props.status) {
      case 'reserved':
        return '#f59e0b'; // Orange für reserviert
      case 'sold':
        return '#ef4444'; // Rot für verkauft
      default:
        return '#059669'; // Grün für verfügbar
    }
  }};

  @media (max-width: 850px) {
    font-size: 12px;
  }
`;

const SizeDisplay = styled.div`
  font-weight: 500;
  color: #374151;
  text-align: center;
`;

const RoomsDisplay = styled.div`
  font-weight: 500;
  color: #374151;
  text-align: center;
`;

const FloorDisplay = styled.div`
  font-weight: 500;
  color: #374151;
  text-align: center;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 500;
  text-align: center;
  display: inline-block;
  min-width: 80px;
  
  ${props => {
    switch (props.status) {
      case 'available':
        return `
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
        `;
      case 'reserved':
        return `
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: white;
        `;
      case 'sold':
        return `
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: white;
        `;
      default:
        return `
          background: linear-gradient(135deg, #6b7280, #4b5563);
          color: white;
        `;
    }
  }}

  @media (max-width: 850px) {
    padding: 4px 8px;
    font-size: 11px;
    min-width: 70px;
  }
`;

const DetailsButton = styled.button`
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }

  @media (max-width: 850px) {
    padding: 6px 12px;
    font-size: 11px;
  }
`;

const HeaderButtonsContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 8px;
  z-index: 101;

  @media (max-width: 850px) {
    top: 12px;
    right: 12px;
    gap: 6px;
  }
`;

const ActionButton = styled.button`
  background: #f1f3f4;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  color: #5f6368;
  transition: all 0.2s ease;
  line-height: 1;
  padding: 0;
  text-align: center;

  &:hover {
    background: #e8eaed;
    transform: translateY(-1px);
  }

  @media (max-width: 850px) {
    width: 48px;
    height: 48px;
    font-size: 20px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4);
    color: #374151;
    font-weight: 600;
    min-height: 48px;
    min-width: 48px;
    
    &:hover {
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.95));
      transform: scale(1.05);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    }
    
    &:active {
      transform: scale(0.95);
      transition: transform 0.1s ease;
    }
  }
`;

const CloseButton = styled(ActionButton)`
  &:hover {
    background: #e8eaed;
    transform: rotate(90deg);
  }

  @media (max-width: 850px) {
    &:hover {
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.95));
      transform: scale(1.05);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    }
  }
`;

const DownloadButton = styled.button`
  background: linear-gradient(135deg, #059669, #047857);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.2);

  &:hover {
    background: linear-gradient(135deg, #047857, #065f46);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(5, 150, 105, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 850px) {
    padding: 10px 16px;
    font-size: 13px;
    border-radius: 10px;
  }
`;

interface ApartmentTableViewProps {
  apartments: Apartment[];
  selectedApartment: Apartment | null;
  onApartmentSelect: (apartment: Apartment) => void;
  onClose: () => void;
}

const ApartmentTableView: React.FC<ApartmentTableViewProps> = ({
  apartments,
  selectedApartment,
  onApartmentSelect,
  onClose,
}) => {
  const tableRef = useRef<HTMLDivElement>(null);
  // Zentrale Preisformatierung wird jetzt verwendet

  const getFloorLabel = (floor: number) => {
    switch (floor) {
      case 0: return 'EG';
      case 1: return '1. OG';
      case 2: return '2. OG';
      case 3: return 'Attika';
      default: return `${floor}. OG`;
    }
  };

  const getStatus = (apartment: Apartment): string => {
    if (apartment.status) {
      return apartment.status;
    }
    // Fallback to old available property
    return apartment.available !== false ? 'available' : 'sold';
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'available': return 'VERFÜGBAR';
      case 'reserved': return 'RESERVIERT';
      case 'sold': return 'VERKAUFT';
      default: return 'UNBEKANNT';
    }
  };

  const exportToPDF = async () => {
    try {
      const pdf = new jsPDF({
        orientation: 'portrait', // Hochformat
        unit: 'mm',
        format: 'a4'
      });

      // PDF-Einstellungen
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (2 * margin);
      
      // Titel hinzufügen
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Leben am Wildbach - Gesamtübersicht', pageWidth / 2, 25, { align: 'center' });
      
      // Datum hinzufügen
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const currentDate = new Date().toLocaleDateString('de-CH');
      pdf.text(`Erstellt am: ${currentDate}`, pageWidth / 2, 35, { align: 'center' });

      // Tabellen-Header
      let yPosition = 50;
      const rowHeight = 8;
      const headerHeight = 10;
      
      // Spaltenbreiten (optimiert für Hochformat)
      const colWidths = {
        nr: 30,
        zimmer: 20,
        etage: 25,
        flaeche: 30,
        preis: 40,
        status: 35
      };

      // Header zeichnen
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPosition, contentWidth, headerHeight, 'F');
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      
      let xPosition = margin + 2;
      pdf.text('NR.', xPosition, yPosition + 7);
      xPosition += colWidths.nr;
      pdf.text('ZIMMER', xPosition, yPosition + 7);
      xPosition += colWidths.zimmer;
      pdf.text('ETAGE', xPosition, yPosition + 7);
      xPosition += colWidths.etage;
      pdf.text('FLÄCHE', xPosition, yPosition + 7);
      xPosition += colWidths.flaeche;
      pdf.text('PREIS', xPosition, yPosition + 7);
      xPosition += colWidths.preis;
      pdf.text('STATUS', xPosition, yPosition + 7);

      yPosition += headerHeight;

      // Tabellen-Daten
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      
      apartments.forEach((apartment, index) => {
        // Prüfen ob neue Seite benötigt wird
        if (yPosition + rowHeight > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin + 10;
        }

        const status = getStatus(apartment);
        
        // Zeilen-Hintergrund (abwechselnd)
        if (index % 2 === 0) {
          pdf.setFillColor(250, 250, 250);
          pdf.rect(margin, yPosition, contentWidth, rowHeight, 'F');
        }

        // Zellen-Inhalt
        xPosition = margin + 2;
        
        // NR.
        const apartmentNr = apartment.name.replace('Wohnung ', '').split(' - ')[0] || apartment.name;
        pdf.text(apartmentNr, xPosition, yPosition + 6);
        xPosition += colWidths.nr;
        
        // ZIMMER
        pdf.text(apartment.rooms.toString(), xPosition, yPosition + 6);
        xPosition += colWidths.zimmer;
        
        // ETAGE
        pdf.text(getFloorLabel(apartment.floor), xPosition, yPosition + 6);
        xPosition += colWidths.etage;
        
        // FLÄCHE
        pdf.text(`${apartment.size} m²`, xPosition, yPosition + 6);
        xPosition += colWidths.flaeche;
        
        // PREIS
        if (status === 'available') {
          const priceText = formatPrice(apartment.price);
          pdf.text(priceText, xPosition, yPosition + 6);
        } else {
          // Farbe für Status-Text setzen
          switch (status) {
            case 'reserved':
              pdf.setTextColor(245, 158, 11); // Orange
              break;
            case 'sold':
              pdf.setTextColor(239, 68, 68); // Rot
              break;
          }
          pdf.text(getStatusLabel(status), xPosition, yPosition + 6);
          pdf.setTextColor(0, 0, 0); // Zurück zu Schwarz
        }
        xPosition += colWidths.preis;
        
        // STATUS
        const statusText = getStatusLabel(status);
        // Farbe je nach Status
        switch (status) {
          case 'available':
            pdf.setTextColor(0, 128, 0); // Grün
            break;
          case 'reserved':
            pdf.setTextColor(255, 140, 0); // Orange
            break;
          case 'sold':
            pdf.setTextColor(255, 0, 0); // Rot
            break;
          default:
            pdf.setTextColor(128, 128, 128); // Grau
        }
        pdf.text(statusText, xPosition, yPosition + 6);
        pdf.setTextColor(0, 0, 0); // Zurück zu Schwarz

        yPosition += rowHeight;
      });

      // Zusätzliche Zeile für Tiefgaragenplätze
      if (yPosition + rowHeight > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin + 10;
      }

      // Zeilen-Hintergrund (abwechselnd basierend auf Anzahl der Apartments)
      if (apartments.length % 2 === 0) {
        pdf.setFillColor(250, 250, 250);
        pdf.rect(margin, yPosition, contentWidth, rowHeight, 'F');
      }

      xPosition = margin + 2;
      
      // NR.
      pdf.setFont('helvetica', 'bold');
      pdf.text('Tiefgaragenplätze', xPosition, yPosition + 6);
      pdf.setFont('helvetica', 'normal');
      xPosition += colWidths.nr;
      
      // ZIMMER
      pdf.text('-', xPosition, yPosition + 6);
      xPosition += colWidths.zimmer;
      
      // ETAGE
      pdf.text('UG', xPosition, yPosition + 6);
      xPosition += colWidths.etage;
      
      // FLÄCHE
      pdf.text('-', xPosition, yPosition + 6);
      xPosition += colWidths.flaeche;
      
      // PREIS
      pdf.text('CHF 40\'000', xPosition, yPosition + 6);
      xPosition += colWidths.preis;
      
      // STATUS
      pdf.setTextColor(0, 128, 0); // Grün
      pdf.text('VERFÜGBAR', xPosition, yPosition + 6);
      pdf.setTextColor(0, 0, 0); // Zurück zu Schwarz

      yPosition += rowHeight;

      // Tabellen-Rahmen zeichnen
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.1);
      
      // Vertikale Linien
      xPosition = margin;
      pdf.line(xPosition, 50, xPosition, Math.min(yPosition, pageHeight - margin));
      xPosition += colWidths.nr;
      pdf.line(xPosition, 50, xPosition, Math.min(yPosition, pageHeight - margin));
      xPosition += colWidths.zimmer;
      pdf.line(xPosition, 50, xPosition, Math.min(yPosition, pageHeight - margin));
      xPosition += colWidths.etage;
      pdf.line(xPosition, 50, xPosition, Math.min(yPosition, pageHeight - margin));
      xPosition += colWidths.flaeche;
      pdf.line(xPosition, 50, xPosition, Math.min(yPosition, pageHeight - margin));
      xPosition += colWidths.preis;
      pdf.line(xPosition, 50, xPosition, Math.min(yPosition, pageHeight - margin));
      xPosition += colWidths.status;
      pdf.line(xPosition, 50, xPosition, Math.min(yPosition, pageHeight - margin));

      // Horizontale Linien
      pdf.line(margin, 50, margin + contentWidth, 50); // Header oben
      pdf.line(margin, 60, margin + contentWidth, 60); // Header unten
      pdf.line(margin, Math.min(yPosition, pageHeight - margin), margin + contentWidth, Math.min(yPosition, pageHeight - margin)); // Tabelle unten

      // Fußzeile mit Kontaktinformationen
      const footerStartY = Math.max(yPosition + 20, pageHeight - 50);
      
      // Kontaktinformationen (ohne Gesamt-Info)
      const contactStartY = footerStartY;
      
      // Trennlinie
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.2);
      pdf.line(margin, contactStartY - 5, pageWidth - margin, contactStartY - 5);
      
      // Kontakt-Header
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Verkauf & Beratung', pageWidth / 2, contactStartY + 5, { align: 'center' });
      
      // Kontaktperson
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(60, 60, 60);
      pdf.text('Csaba von Zalan', pageWidth / 2, contactStartY + 12, { align: 'center' });
      
      // Telefon und Email
      pdf.setFontSize(8);
      pdf.setTextColor(80, 80, 80);
      pdf.text('Telefon: 043 222 35 22', pageWidth / 2, contactStartY + 19, { align: 'center' });
      pdf.text('Email: zalan@vonzalan-partner.ch', pageWidth / 2, contactStartY + 25, { align: 'center' });

      // Datei speichern
      const currentDateForFilename = new Date().toLocaleDateString('de-CH').replace(/\./g, '-');
      const filename = `Wildbach_Apartmentliste_${currentDateForFilename}.pdf`;
      
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Fehler beim Erstellen der PDF-Datei. Bitte versuchen Sie es erneut.');
    }
  };

  return (
    <TableContainer>
      <HeaderButtonsContainer>
        <DownloadButton onClick={exportToPDF} title="Als PDF herunterladen">
          <Download size={16} />
          Preisliste herunterladen
        </DownloadButton>
        <CloseButton onClick={onClose} title="Liste schließen">
          <X size={16} />
        </CloseButton>
      </HeaderButtonsContainer>
      <ScrollableContent>
        <div ref={tableRef}>
          <ApartmentTable>
          <TableHeader>
            <tr>
              <HeaderCell>NR.</HeaderCell>
              <HeaderCell>ZIMMER</HeaderCell>
              <HeaderCell>ETAGE</HeaderCell>
              <HeaderCell>FLÄCHE</HeaderCell>
              <HeaderCell>PREIS</HeaderCell>
              <HeaderCell>STATUS</HeaderCell>
              <HeaderCell>DETAILS</HeaderCell>
            </tr>
          </TableHeader>
          <TableBody>
            {apartments.map((apartment) => {
              const status = getStatus(apartment);
              const isSelected = selectedApartment?.id === apartment.id;
              const isAvailable = status === 'available';
              
              return (
                <TableRow
                  key={apartment.id}
                  isSelected={isSelected}
                  isAvailable={isAvailable}
                  onClick={() => onApartmentSelect(apartment)}
                >
                  <TableCell>
                    <ApartmentNumber>
                      {apartment.name.replace('Wohnung ', '').split(' - ')[0] || apartment.name}
                    </ApartmentNumber>
                    <ApartmentDescription>
                      {apartment.name.split(' - ')[1] || ''}
                    </ApartmentDescription>
                  </TableCell>
                  <TableCell>
                    <RoomsDisplay>{apartment.rooms}</RoomsDisplay>
                  </TableCell>
                  <TableCell>
                    <FloorDisplay>{getFloorLabel(apartment.floor)}</FloorDisplay>
                  </TableCell>
                  <TableCell>
                    <SizeDisplay>{apartment.size} m²</SizeDisplay>
                  </TableCell>
                  <TableCell>
                    {status === 'available' ? (
                      <PriceDisplay>{formatPrice(apartment.price)}</PriceDisplay>
                    ) : (
                      <StatusPriceDisplay status={status}>
                        {getStatusLabel(status)}
                      </StatusPriceDisplay>
                    )}
                  </TableCell>
                  <TableCell style={{ textAlign: 'center' }}>
                    <StatusBadge status={status}>
                      {getStatusLabel(status)}
                    </StatusBadge>
                  </TableCell>
                  <TableCell style={{ textAlign: 'center' }}>
                    <DetailsButton onClick={(e) => {
                      e.stopPropagation();
                      onApartmentSelect(apartment);
                    }}>
                      DETAILS
                    </DetailsButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {/* Zusätzliche Zeile für Tiefgaragenplätze */}
            <TableRow isAvailable={true}>
              <TableCell>
                <ApartmentNumber>Tiefgaragenplätze</ApartmentNumber>
              </TableCell>
              <TableCell>
                <RoomsDisplay>-</RoomsDisplay>
              </TableCell>
              <TableCell>
                <FloorDisplay>UG</FloorDisplay>
              </TableCell>
              <TableCell>
                <SizeDisplay>-</SizeDisplay>
              </TableCell>
              <TableCell>
                <PriceDisplay>CHF 40'000</PriceDisplay>
              </TableCell>
              <TableCell style={{ textAlign: 'center' }}>
                <StatusBadge status="available">
                  VERFÜGBAR
                </StatusBadge>
              </TableCell>
              <TableCell style={{ textAlign: 'center' }}>
                <DetailsButton disabled style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                  -
                </DetailsButton>
              </TableCell>
            </TableRow>
          </TableBody>
          </ApartmentTable>
        </div>
      </ScrollableContent>
    </TableContainer>
  );
};

export default ApartmentTableView;
